import React, { useEffect, useMemo, useState } from 'react'
import {
	Box,
	CircularProgress,
	Paper,
	Tooltip,
	Typography,
	alpha,
} from '@mui/material'
import { AppCardTitle, AppEmptyLine, AppNote, AppPillToggle, AppResultsSkeleton } from '@components'
import { dsColorNew, dataColors } from '@palette'
import { heldHeightSx, useHeldHeight } from 'Pages/MyBusiness/_shared/Ui'

/**
 * Peak Calendar heatmap.
 *
 *   Rows:    (office, SIPP) pairs sorted by avg total fleet (the
 *            biggest pots of capacity first).
 *   Columns: days in the filter window.
 *   Cells:   coloured by peak_utilization_pct (max simultaneous
 *            unavailable / total fleet that day). Hover shows the
 *            raw numbers.
 *
 *   <PeakCalendar offices={['MAD']} categories={['ECMR']}
 *                 from="2026-06-01" to="2026-06-30"/>
 */
const CELL_W   = 18

const VIEW_LABEL = {
	'by-office':   'offices',
	'by-category': 'SIPP categories',
	'detail':      'office × SIPP combos',
}

// Util % → background. Sequential green→amber→red so the eye picks
// out the hot cells at a glance.
function colourFor(pct) {
	if (pct == null) return dataColors.slate100   // empty
	if (pct < 25)    return dataColors.green100   // very low — soft green
	if (pct < 50)    return dataColors.green200
	if (pct < 70)    return dsColorNew.noteAmberBorder   // amber
	if (pct < 85)    return dataColors.orange300
	if (pct < 95)    return dsColorNew.dangerSoft   // hot
	return dsColorNew.dangerRed                     // saturated red
}

function fmtDay(iso) {
	return iso ? iso.slice(8, 10) : ''
}
function fmtMonthLabel(iso) {
	if (!iso) return ''
	return new Intl.DateTimeFormat('es-ES', { month: 'short' }).format(new Date(iso))
}

export default function PeakCalendar({ offices = [], categories = [], from, to }) {
	const [view,    setView]    = useState('by-office')
	const [data,    setData]    = useState(null)
	const [loading, setLoading] = useState(true)
	const [error,   setError]   = useState(null)
	const [cardRef, heldHeight] = useHeldHeight()

	useEffect(() => {
		if (!from || !to) return
		setLoading(true); setError(null)
		const qs = new URLSearchParams({ from, to, view })
		if (offices.length)    qs.set('offices', offices.join(','))
		if (categories.length) qs.set('sipps',   categories.join(','))
		fetch(`/api/my-business/peak-calendar?${qs}`, { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}, [from, to, view, offices.join(','), categories.join(',')])

	// All hooks must run on every render (Rules of Hooks). Compute the
	// month boundaries from whatever days we have — empty array when
	// data is still loading.
	const days = data?.days || []
	const monthBoundaries = useMemo(() => {
		const out = []
		let lastMonth = null
		days.forEach((d, i) => {
			const m = d.slice(0, 7)
			if (m !== lastMonth) { out.push({ idx: i, label: fmtMonthLabel(d) }); lastMonth = m }
		})
		return out
	}, [days.join(',')])

	if (error) return (
		<Paper sx={{ p: 2.5 }}>
			<AppNote severity="error">Peak Calendar unreachable: {error}</AppNote>
		</Paper>
	)
	// Loading (also a refetch): the card's skeleton, so nothing can be clicked half-loaded
	if (loading || !data) return <AppResultsSkeleton rows={10} columns={8} sx={heldHeightSx(heldHeight)}/>
	if (!data.rows?.length) return (
		<Paper sx={{ p: 2.5 }}>
			<AppEmptyLine>No peak data for this window. Try widening the filters.</AppEmptyLine>
		</Paper>
	)

	return (
		<Paper ref={cardRef} sx={{ p: 2.5 }}>
			<Box display="flex" alignItems="center" justifyContent="space-between"
			     flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
				<Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
					<AppCardTitle icon="calendar-week" title={`Peak utilization · ${data.rows.length} ${VIEW_LABEL[view]}`}/>
					<AppPillToggle size="small" value={view} onChange={setView} options={[
						{ value: 'by-office', label: 'By Office' },
						{ value: 'by-category', label: 'By Category' },
						{ value: 'detail', label: 'Detail' },
					]}/>
				</Box>
				<Legend/>
			</Box>

			<Box sx={{ overflowX: 'auto', maxWidth: '100%', border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px' }}>
				<Box component="table" sx={tableSx}>
					<thead>
						<tr>
							<th rowSpan={2} className="fx f1">{view === 'by-office' ? 'Office' : 'SIPP'}</th>
							<th rowSpan={2} className="fx f2">Fleet</th>
							<th rowSpan={2} className="fx f3">Peak</th>
							{monthBoundaries.map((m, i) => {
								const next = monthBoundaries[i + 1]
								return <th key={i} className="mo" colSpan={(next ? next.idx : days.length) - m.idx}>{m.label}</th>
							})}
						</tr>
						<tr>
							{days.map((d) => <th key={d} className="dy">{fmtDay(d)}</th>)}
						</tr>
					</thead>
					<tbody>
						{groupRows(view, data.rows).map((item) => item.office ? (
							<tr key={`office-${item.office}`} className="grp">
								<td className="fx f1" colSpan={3}>
									<b>{item.office}</b>
									{item.name && <Box component="span" sx={{ color: dsColorNew.textMuted }}> · {item.name}</Box>}
								</td>
								<td colSpan={days.length}/>
							</tr>
						) : (
							<PeakRow key={`${item.label_primary}-${item.label_secondary}`} row={item} view={view} days={days}/>
						))}
					</tbody>
				</Box>
			</Box>

			<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted, mt: 1.5, '& strong': { color: dsColorNew.textDark } }}>
				Each cell is the day's <strong>peak unavailability</strong> for that
				office &amp; SIPP combo — the worst moment of the day, not the
				average. Dark red cells mean you ran at or near full capacity.
				Hover for raw numbers.
			</Typography>
		</Paper>
	)
}

// The rows of the heatmap. Detail groups the office × SIPP combos under a grey row per office,
// offices in the order of their biggest combo; the other views are flat.
function groupRows(view, rows) {
	if (view !== 'detail') return rows
	const byOffice = new Map()
	rows.forEach(r => {
		const key = r.office_code || r.label_primary
		if (!byOffice.has(key)) byOffice.set(key, { office: key, name: r.office_name || '', rows: [] })
		byOffice.get(key).rows.push(r)
	})
	return [...byOffice.values()].flatMap(g => [{ office: g.office, name: g.name }, ...g.rows])
}

// Fixed white columns and navy header, as the price tables of Business Intelligence
const FIX_W = { f1: 220, f2: 64, f3: 84 }
const tableSx = {
	borderCollapse: 'separate', borderSpacing: 0, fontSize: 13, minWidth: '100%',
	'& th': { backgroundColor: dsColorNew.tableHead, color: dsColorNew.white, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', p: 0 },
	'& th.mo': { textAlign: 'left', px: 0.75, pt: 1, pb: 0.25, textTransform: 'capitalize', borderLeft: `1px solid ${alpha(dsColorNew.white, 0.18)}` },
	'& th.dy': { fontWeight: 400, fontSize: 10, color: dataColors.onNavyMuted, width: CELL_W, minWidth: CELL_W, textAlign: 'center', pb: 0.75 },
	'& th.fx': { px: 1.5, textAlign: 'left', fontWeight: 500, color: alpha(dsColorNew.white, 0.8), zIndex: 3, backgroundColor: dsColorNew.tableHead },
	'& td': { p: 0, height: 30, borderBottom: `1px solid ${dataColors.tableLineFaint}` },
	'& .fx': { position: 'sticky', backgroundColor: dsColorNew.white, zIndex: 2, whiteSpace: 'nowrap', px: 1.5, boxSizing: 'border-box' },
	'& .f1': { left: 0, width: FIX_W.f1, minWidth: FIX_W.f1, maxWidth: FIX_W.f1, overflow: 'hidden', textOverflow: 'ellipsis' },
	'& .f2': { left: FIX_W.f1, width: FIX_W.f2, minWidth: FIX_W.f2, textAlign: 'right' },
	'& .f3': { left: FIX_W.f1 + FIX_W.f2, width: FIX_W.f3, minWidth: FIX_W.f3, boxShadow: `6px 0 8px -6px ${alpha(dsColorNew.textTitle, 0.18)}` },
	'& tr.grp td': { backgroundColor: dsColorNew.pillTrackBg, height: 28, fontSize: 12.5, color: dsColorNew.textTitle, borderBottom: `1px solid ${dsColorNew.grayBorder}` },
	'& tr.grp td.fx': { boxShadow: 'none', maxWidth: 'none', overflow: 'visible' },
	'& tbody tr:not(.grp):hover td': { backgroundColor: dsColorNew.hoverBgLight },
	'& thead th.fx': { backgroundColor: dsColorNew.tableHead },
}

function PeakRow({ row, view, days }) {
	const byDate = new Map(row.cells.map(c => [c.date, c]))
	const fleetAvg = Math.round(row.total_fleet_avg)
	return (
		<tr>
			<td className="fx f1" title={row.label_secondary || ''} style={{ paddingLeft: view === 'detail' ? 28 : undefined }}>
				<Box component="span" sx={{ fontWeight: 600, color: dsColorNew.textTitle }}>
					{view === 'detail' ? row.label_secondary : row.label_primary}
				</Box>
				{view !== 'detail' && row.label_secondary && (
					<Box component="span" sx={{ color: dsColorNew.textMuted, ml: 0.75 }}>{row.label_secondary}</Box>
				)}
			</td>
			<td className="fx f2">
				<Tooltip arrow placement="top"
				         title="Average size of the physical fleet assigned to this row across the selected window.">
					<Box component="span" sx={{ cursor: 'help', fontVariantNumeric: 'tabular-nums', color: dsColorNew.textDark }}>{fleetAvg}</Box>
				</Tooltip>
			</td>
			<td className="fx f3">
				<Tooltip arrow placement="top"
				         title="Highest daily peak utilization in the window — the worst moment of any day.">
					<Box component="span" sx={{
						display: 'inline-flex', alignItems: 'center', height: 20, px: 1, borderRadius: '10px',
						bgcolor: colourFor(row.max_util_pct),
						color: row.max_util_pct >= 70 ? dsColorNew.white : dsColorNew.textTitle,
						fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', cursor: 'help',
					}}>
						{`${row.max_util_pct}%`}
					</Box>
				</Tooltip>
			</td>
			{days.map((d) => {
				const cell = byDate.get(d)
				return (
					<Box component="td" key={d} sx={{ width: CELL_W, minWidth: CELL_W, textAlign: 'center' }}>
						<Tooltip title={<CellTooltip date={d}
						                             rowPrimary={row.label_primary}
						                             rowSecondary={row.label_secondary}
						                             cell={cell}/>}
						         arrow placement="top"
						         componentsProps={{
							         tooltip: { sx: {
								         bgcolor: dsColorNew.white,
								         color: dsColorNew.textDark,
								         boxShadow: `0 8px 24px ${alpha(dsColorNew.navy, 0.12)}`,
								         border: `1px solid ${dsColorNew.grayBorder}`,
								         borderRadius: '10px',
								         p: 0, maxWidth: 'none',
							         }},
							         arrow: { sx: { color: dsColorNew.white, '&:before': { border: `1px solid ${dsColorNew.grayBorder}` } }},
						         }}>
							<Box sx={{
								width: CELL_W - 4, height: 20, mx: 'auto',
								bgcolor: colourFor(cell?.util_pct),
								borderRadius: '3px',
								transition: 'transform 80ms ease',
								'&:hover': { transform: 'scale(1.25)' },
							}}/>
						</Tooltip>
					</Box>
				)
			})}
		</tr>
	)
}

function CellTooltip({ date, rowPrimary, rowSecondary, cell }) {
	const dateLabel = new Intl.DateTimeFormat('es-ES', {
		weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
	}).format(new Date(date))
	if (!cell) {
		return (
			<Box sx={{ p: 1.5, minWidth: 220 }}>
				<Box sx={{ fontSize: 13, fontWeight: 600, color: dsColorNew.textTitle, mb: 0.5 }}>{dateLabel}</Box>
				<Box sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
					No data for this day.
				</Box>
			</Box>
		)
	}
	const util = cell.util_pct
	const bg = util == null ? dataColors.slate100
		: util < 25 ? dataColors.green100
		: util < 50 ? dataColors.green200
		: util < 70 ? dsColorNew.noteAmberBorder
		: util < 85 ? dataColors.orange300
		: util < 95 ? dsColorNew.dangerSoft
		: dsColorNew.dangerRed
	const isHot = util >= 70
	const idle  = Math.max(0, cell.total_fleet - cell.peak_rented - cell.peak_maintenance)
	const seg = (val) => cell.total_fleet > 0
		? `${(val / cell.total_fleet * 100).toFixed(1)}%`
		: '0%'
	return (
		<Box sx={{ p: 1.5, minWidth: 260 }}>
			<Box sx={{ fontSize: 13, fontWeight: 600, color: dsColorNew.textTitle, lineHeight: 1.3 }}>
				{dateLabel}
			</Box>
			<Box sx={{ fontSize: 12, color: dsColorNew.textMuted, mb: 1 }}>
				<Box component="span" sx={{ fontWeight: 600, color: dsColorNew.textDark }}>
					{rowPrimary}
				</Box>
				{rowSecondary && <> · {rowSecondary}</>}
			</Box>
			{/* Utilization headline */}
			<Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.25 }}>
				<Box sx={{
					bgcolor: bg, color: isHot ? dsColorNew.white : dsColorNew.textTitle,
					px: 1.25, py: 0.5, borderRadius: '8px',
					fontSize: 20, fontWeight: 700, lineHeight: 1.1,
				}}>
					{util}%
				</Box>
				<Box sx={{ fontSize: 11.5, color: dsColorNew.textMuted, lineHeight: 1.25 }}>
					peak<br/>utilization
				</Box>
			</Box>
			{/* Mini stacked bar — rented + maintenance + idle */}
			<Box sx={{
				display: 'flex', height: 10, borderRadius: '5px', overflow: 'hidden',
				border: `1px solid ${dsColorNew.grayBorder}`, mb: 1,
			}}>
				{cell.peak_rented > 0 && (
					<Box sx={{ flex: cell.peak_rented, bgcolor: dataColors.red500 }}/>
				)}
				{cell.peak_maintenance > 0 && (
					<Box sx={{ flex: cell.peak_maintenance, bgcolor: dsColorNew.warningBar }}/>
				)}
				{idle > 0 && (
					<Box sx={{ flex: idle, bgcolor: dataColors.slate200 }}/>
				)}
			</Box>
			{/* Breakdown rows */}
			<Box sx={{ fontSize: 12.5, color: dsColorNew.textDark, display: 'grid',
			           gridTemplateColumns: 'auto 1fr auto', columnGap: 1, rowGap: 0.25 }}>
				<Box sx={{ width: 10, height: 10, bgcolor: dataColors.red500, borderRadius: '2px',
				           alignSelf: 'center' }}/>
				<Box>Rented</Box>
				<Box sx={{ fontWeight: 600, color: dsColorNew.textTitle }}>{cell.peak_rented} / {cell.total_fleet}
					<Box component="span" sx={{ color: dsColorNew.textMuted, fontWeight: 400, ml: 0.5 }}>
						({seg(cell.peak_rented)})
					</Box>
				</Box>
				<Box sx={{ width: 10, height: 10, bgcolor: dsColorNew.warningBar, borderRadius: '2px',
				           alignSelf: 'center' }}/>
				<Box>Maintenance</Box>
				<Box sx={{ fontWeight: 600, color: dsColorNew.textTitle }}>{cell.peak_maintenance}
					<Box component="span" sx={{ color: dsColorNew.textMuted, fontWeight: 400, ml: 0.5 }}>
						({seg(cell.peak_maintenance)})
					</Box>
				</Box>
				<Box sx={{ width: 10, height: 10, bgcolor: dataColors.slate200, borderRadius: '2px',
				           alignSelf: 'center' }}/>
				<Box sx={{ color: dsColorNew.textMuted }}>Idle</Box>
				<Box sx={{ color: dsColorNew.textMuted }}>{idle}
					<Box component="span" sx={{ ml: 0.5 }}>({seg(idle)})</Box>
				</Box>
			</Box>
		</Box>
	)
}

function Legend() {
	const stops = [
		{ pct: '<25%',  c: colourFor(10) },
		{ pct: '25-50', c: colourFor(40) },
		{ pct: '50-70', c: colourFor(60) },
		{ pct: '70-85', c: colourFor(78) },
		{ pct: '85-95', c: colourFor(90) },
		{ pct: '≥95',   c: colourFor(97) },
	]
	return (
		<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
				Utilization:
			</Typography>
			{stops.map((s) => (
				<Box key={s.pct} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<Box sx={{ width: 12, height: 12, bgcolor: s.c, borderRadius: '3px' }}/>
					<Typography sx={{ fontSize: 11.5, color: dsColorNew.textDark }}>
						{s.pct}
					</Typography>
				</Box>
			))}
		</Box>
	)
}
